import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X, EyeOff, Eye } from "lucide-react";
import { CATEGORIES } from "@/data/categories";
import { API } from "@/lib/api";
import { BENEFIT_ICONS, BENEFIT_ICON_LABELS, invalidateBenefitCache } from "@/hooks/useBenefits";

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors text-sm";

const EMPTY = { title: "", description: "", icon: "star", enabled: true, order: 0, scope: "all", category_ids: [], vehicle_ids: [] };

const SCOPE_LABELS = { all: "ყველა ავტომობილი", categories: "არჩეული კატეგორიები", vehicles: "არჩეული ავტომობილები" };

function BenefitForm({ initial, vehicles, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const [vQuery, setVQuery] = useState("");

  const toggleIn = (key, id) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
    }));
  };

  const filteredVehicles = useMemo(() => {
    const q = vQuery.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((v) => `${v.internal_name} ${v.public_title} ${v.year}`.toLowerCase().includes(q));
  }, [vehicles, vQuery]);

  return (
    <form
      data-testid="admin-benefit-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ ...form, order: Number(form.order) || 0 });
      }}
      className="card-lux p-6 mb-8 space-y-4"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">სათაური <span className="text-[#D4AF37]">*</span></label>
          <input data-testid="admin-benefit-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="მაგ. სპეციალური შეღავათი დატენვაზე" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">იკონი</label>
          <select data-testid="admin-benefit-icon" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className={inputCls}>
            {Object.entries(BENEFIT_ICON_LABELS).map(([k, v]) => (
              <option key={k} value={k} className="bg-[#141721]">{v}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-400 mb-1.5">აღწერა (ჩანს მანქანის დეტალურ გვერდზე)</label>
          <textarea data-testid="admin-benefit-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">ვის ეხება</label>
          <select data-testid="admin-benefit-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className={inputCls}>
            {Object.entries(SCOPE_LABELS).map(([k, v]) => (
              <option key={k} value={k} className="bg-[#141721]">{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">რიგითობა (პატარა ჯერ ჩანს)</label>
          <input data-testid="admin-benefit-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={inputCls} />
        </div>
      </div>

      {form.scope === "categories" && (
        <div>
          <label className="block text-xs text-gray-400 mb-2">კატეგორიები</label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {CATEGORIES.map((c) => (
              <label key={c.id} className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:border-[#D4AF37]/40 transition-colors">
                <input type="checkbox" data-testid={`admin-benefit-cat-${c.id}`} checked={form.category_ids.includes(c.id)} onChange={() => toggleIn("category_ids", c.id)} className="w-4 h-4 accent-[#D4AF37]" />
                {c.name} {c.years}
              </label>
            ))}
          </div>
        </div>
      )}

      {form.scope === "vehicles" && (
        <div>
          <label className="block text-xs text-gray-400 mb-2">ავტომობილები ({form.vehicle_ids.length} არჩეული)</label>
          <input data-testid="admin-benefit-vehicle-search" value={vQuery} onChange={(e) => setVQuery(e.target.value)} className={`${inputCls} mb-2`} placeholder="ძებნა მანქანით..." />
          <div className="max-h-48 overflow-y-auto grid sm:grid-cols-2 gap-2 pr-1">
            {filteredVehicles.map((v) => (
              <label key={v.id} className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:border-[#D4AF37]/40 transition-colors">
                <input type="checkbox" data-testid={`admin-benefit-veh-${v.id}`} checked={form.vehicle_ids.includes(v.id)} onChange={() => toggleIn("vehicle_ids", v.id)} className="w-4 h-4 accent-[#D4AF37]" />
                {v.internal_name || v.public_title} · {v.year}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" data-testid="admin-benefit-enabled" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
          აქტიურია
        </label>
        <button type="submit" disabled={saving} data-testid="admin-benefit-save" className="btn-gold !py-2.5 !px-6 text-sm disabled:opacity-60">
          {saving ? "ინახება..." : "შენახვა"}
        </button>
        <button type="button" onClick={onCancel} data-testid="admin-benefit-cancel" className="btn-ghost !py-2.5 !px-6 text-sm">
          გაუქმება
        </button>
      </div>
    </form>
  );
}

export default function AdminBenefits({ auth }) {
  const [benefits, setBenefits] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/benefits`, auth).then((r) => setBenefits(r.data)).catch(() => {});
    axios.get(`${API}/admin/vehicles`, auth).then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    setSaving(true);
    try {
      if (editingId) {
        const r = await axios.put(`${API}/benefits/${editingId}`, form, auth);
        setBenefits((prev) => prev.map((b) => (b.id === editingId ? r.data : b)));
        toast.success("შეღავათი განახლდა");
      } else {
        const r = await axios.post(`${API}/benefits`, form, auth);
        setBenefits((prev) => [...prev, r.data].sort((a, b) => a.order - b.order));
        toast.success("შეღავათი დაემატა");
      }
      invalidateBenefitCache();
      setAdding(false);
      setEditingId(null);
    } catch {
      toast.error("შენახვა ვერ მოხერხდა");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (b) => {
    try {
      const r = await axios.put(`${API}/benefits/${b.id}`, { ...b, enabled: !b.enabled }, auth);
      setBenefits((prev) => prev.map((x) => (x.id === b.id ? r.data : x)));
      invalidateBenefitCache();
      toast.success(r.data.enabled ? "შეღავათი ჩაირთო" : "შეღავათი გამოირთო");
    } catch {
      toast.error("ოპერაცია ვერ მოხერხდა");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`ნამდვილად წაიშალოს „${b.title}"?`)) return;
    try {
      await axios.delete(`${API}/benefits/${b.id}`, auth);
      setBenefits((prev) => prev.filter((x) => x.id !== b.id));
      invalidateBenefitCache();
      toast.success("შეღავათი წაიშალა");
    } catch {
      toast.error("წაშლა ვერ მოხერხდა");
    }
  };

  const editing = benefits.find((b) => b.id === editingId);
  const showForm = adding || editing;

  return (
    <div data-testid="admin-benefits-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">შეღავათები ({benefits.length})</h2>
        <button onClick={() => { setAdding(!adding); setEditingId(null); }} data-testid="admin-add-benefit-btn" className="btn-gold !py-2.5 !px-5 text-sm">
          {adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {adding ? "დახურვა" : "ახალი შეღავათი"}
        </button>
      </div>

      {showForm && (
        <BenefitForm
          initial={editing ? { title: editing.title, description: editing.description, icon: editing.icon, enabled: editing.enabled, order: editing.order, scope: editing.scope, category_ids: editing.category_ids, vehicle_ids: editing.vehicle_ids } : EMPTY}
          vehicles={vehicles}
          onSave={save}
          onCancel={() => { setAdding(false); setEditingId(null); }}
          saving={saving}
        />
      )}

      {benefits.length === 0 && !showForm ? (
        <div className="border border-dashed border-white/20 rounded-xl p-12 text-center text-gray-500" data-testid="admin-benefits-empty">
          შეღავათები ჯერ არ არის — დაამატე პირველი
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-benefits-list">
          {benefits.map((b) => {
            const Icon = BENEFIT_ICONS[b.icon] || BENEFIT_ICONS.star;
            return (
              <div key={b.id} className="card-lux p-4 flex items-center gap-4" data-testid={`admin-benefit-${b.id}`}>
                <span className="w-11 h-11 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#E5C378]" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">
                    {b.title}
                    {!b.enabled && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/10 text-gray-400 border border-white/20">გამორთული</span>}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    რიგი: {b.order} · {SCOPE_LABELS[b.scope]}
                    {b.scope === "categories" && ` (${b.category_ids.length})`}
                    {b.scope === "vehicles" && ` (${b.vehicle_ids.length})`}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => toggleEnabled(b)} data-testid={`admin-benefit-toggle-${b.id}`} className="btn-ghost !py-2 !px-3.5 text-xs">
                    {b.enabled ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => { setEditingId(b.id); setAdding(false); }} data-testid={`admin-benefit-edit-${b.id}`} className="btn-ghost !py-2 !px-3.5 text-xs">
                    <Pencil className="w-3.5 h-3.5" /> რედაქტირება
                  </button>
                  <button onClick={() => remove(b)} data-testid={`admin-benefit-delete-${b.id}`} className="!py-2 !px-3.5 text-xs inline-flex items-center gap-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
